import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { TraversalCompleteToast } from "../../TraversalCompleteToast";
import { useSeenRoutes } from "../useSeenRoutes";
import type {
  UseRouteTraversalModeV3Args,
  UseRouteTraversalModeV3Result,
} from "./types";

export const useRouteTraversalModeV3 = ({
  canEdit,
  currentRouteTypeId,
  selectedGroupRoutes,
  isCableGroupApproved,
  toggleCableGroupApproval,
}: UseRouteTraversalModeV3Args): UseRouteTraversalModeV3Result => {
  const { t } = useTranslation();

  const { seenIds, markSeen } = useSeenRoutes({
    routeTypeId: currentRouteTypeId,
  });

  const seenCount = useMemo(
    () =>
      selectedGroupRoutes.filter((route) => seenIds.has(route.id)).length,
    [selectedGroupRoutes, seenIds],
  );

  const showTraversalCompleteToast = useCallback(() => {
    toast(TraversalCompleteToast, {
      autoClose: false,
      position: "bottom-right",
      data: {
        title: t("smartSurveyor.traversal.allSeenTitle"),
        description: t("smartSurveyor.traversal.allSeenDescription"),
        cancelLabel: t("smartSurveyor.common.cancel"),
        approveLabel: t("smartSurveyor.common.yes"),
        onApprove: () => toggleCableGroupApproval(currentRouteTypeId),
      },
    });
  }, [currentRouteTypeId, toggleCableGroupApproval, t]);

  const markSeenAndCheckCompletion = useCallback(
    (routeId: string, remainingCount?: number) => {
      if (!currentRouteTypeId) return;
      const isNewlySeen = !seenIds.has(routeId);
      markSeen(routeId);
      const totalCount = remainingCount ?? selectedGroupRoutes.length;
      if (
        canEdit &&
        isNewlySeen &&
        !isCableGroupApproved(currentRouteTypeId) &&
        seenCount + 1 >= totalCount
      ) {
        showTraversalCompleteToast();
      }
    },
    [
      canEdit,
      currentRouteTypeId,
      seenIds,
      seenCount,
      selectedGroupRoutes,
      markSeen,
      isCableGroupApproved,
      showTraversalCompleteToast,
    ],
  );

  return {
    seenIds,
    seenCount,
    markSeen,
    markSeenAndCheckCompletion,
  };
};
